import { GlobalMessages } from '@shared/localization/messages';
import { Link, useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useEstimationUserAssignmentQueryActions } from '@/api/hooks/estimationUserAssignmentQueryActions';
import type {
  ApiEstimationPermission,
  EstimationAiAssistedReviewRequest,
  EstimationModelResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import ArrowLeftIcon from '@/assets/arrow-left-icon.svg?react';
import IconCpuBolt from '@/assets/Icons/IconCpuBolt';
import { AiReviewParamsModal } from '@/components/Ai/AiReviewParamsModal/AiReviewParamsModal';
import { Button } from '@/components/atoms/Button';
import { EstimationStageBadge } from '@/components/atoms/EstimationStageBadge';
import { Loader } from '@/components/atoms/Loader';
import { NotFound } from '@/components/atoms/NotFound';
import { ArchivedModelBanner } from '@/components/molecules/ArchivedModelBanner';
import type { ITabItemRouted } from '@/components/molecules/Tabs';
import { Tabs } from '@/components/molecules/Tabs';
import { ReviewMessages } from '@/components/organism/NewEstimation/ProjectReview/messages';
import { useEstimation } from '@/context/estimationContext';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { eEstimationDetailTab } from '@/pages/estimation/detail/model';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';

import { ApproveButton } from './ApproveButton';
import { CancelReviewButton } from './CancelReviewButton';
import EstimationVersionDropdown from './EstimationVersionDropdown';
import { estimationTabMessageDefinitions } from './messages';
import { StartReviewButton } from './StartReviewButton';

type RouteTree = typeof Route;

interface LayoutProps {
  buttons?: ReactNode;
}

export const Layout: FCC<LayoutProps> = ({ buttons, children }) => {
  const globalMessages = useMessages(GlobalMessages);
  const reviewMessages = useMessages(ReviewMessages);
  const toast = useToast();
  const { tabId = eEstimationDetailTab.project, estimationId } = Route.useLoaderData();
  const {
    actions: { setDetailedEstimationId },
  } = useEstimation();

  const messages = useMessages(estimationTabMessageDefinitions);
  const navigate = useNavigate();

  const [isAiReviewParamsModalOpen, setIsAiReviewParamsModalOpen] = useState(false);

  const { useGetEstimation, useEnqueueEstimationAiReviewGeneration } = useEstimationQueryActions();
  const { data, isLoading, isError, error } = useGetEstimation(estimationId) as {
    data: ProjectWithEstimationResponse | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };

  const { useGetEstimationModel } = useEstimationModelQueryActions();
  const { data: estimationModel } = useGetEstimationModel(
    data?.estimation.estimationModelId,
    data?.estimation.cultureName,
  ) as { data: EstimationModelResponse | undefined; isLoading: boolean };

  const { useGetEstimationPermissions } = useEstimationUserAssignmentQueryActions();
  const { data: estimationPermissions } = useGetEstimationPermissions(estimationId) as {
    data: ApiEstimationPermission[] | undefined;
    isLoading: boolean;
  };

  const { mutateAsync: enqueueEstimationAiReviewGeneration } =
    useEnqueueEstimationAiReviewGeneration(estimationId) as {
      mutateAsync: (
        params: EstimationAiAssistedReviewRequest,
        options?: unknown,
      ) => Promise<unknown>;
    };

  const hasEstimationPermission = useCallback(
    (permission: ApiEstimationPermission) =>
      estimationPermissions &&
      estimationPermissions.some((x: ApiEstimationPermission) => x === permission),
    [estimationPermissions],
  );

  const tabsConfiguration: ITabItemRouted[] = useMemo(
    () => [
      {
        tabId: eEstimationDetailTab.project,
        label: messages.projectLabel(),
      },
      {
        tabId: eEstimationDetailTab.metadata,
        label: messages.metadataLabel(),
      },
      {
        tabId: eEstimationDetailTab.estimation,
        label: messages.estimationLabel(),
      },
      {
        tabId: eEstimationDetailTab.assumptions,
        label: messages.estimationAssumptionsLabel(),
      },
      {
        tabId: eEstimationDetailTab.resource,
        label: messages.resourceLabel(),
      },
      {
        tabId: eEstimationDetailTab.resourceCost,
        label: messages.resourceCostLabel(),
      },
      {
        tabId: eEstimationDetailTab.directCost,
        label: messages.directCostLabel(),
      },
      {
        tabId: eEstimationDetailTab.costSummary,
        label: messages.costSummaryLabel(),
      },
      {
        tabId: eEstimationDetailTab.managementReport,
        label: messages.managementReportLabel(),
      },
      {
        tabId: eEstimationDetailTab.review,
        label: messages.reviewLabel(),
      },
    ],
    [messages],
  );

  const handleTabChange = useCallback(
    async (selectedTabId: string) => {
      await navigate({
        to: '/estimation/detail/$estimationId/$tabId' as RouteTree['path'],
        params: { estimationId, tabId: selectedTabId },
      });
    },
    [navigate, estimationId],
  );

  const onCloseClick = async () => {
    await navigate({ to: '/' });
  };

  const handleEnqueueAiReview = useCallback(
    async (maxWordsCount: number, cultureName: string) => {
      setIsAiReviewParamsModalOpen(false);
      await enqueueEstimationAiReviewGeneration(
        { maxWordsCount, cultureName },
        {
          onSuccess: () => {
            toast.showToast(reviewMessages.aiReviewSuccessfullyEnqueued(), 'success');
          },
        },
      );
    },
    [enqueueEstimationAiReviewGeneration, reviewMessages, toast],
  );

  useEffect(() => {
    if (estimationId) {
      setDetailedEstimationId(estimationId);
    }
  }, [estimationId, setDetailedEstimationId]);

  useEffect(() => {
    if (data?.estimation.stage === ApiEstimationStage.IN_CREATION) {
      void navigate({ to: '/estimation/$estimationId/continue-wizard', params: { estimationId } });
    }
  }, [data, estimationId, navigate]);

  // Only a 404 means the estimation is gone; the endpoint also answers 401 and 500, and showing
  // "does not exist" for those hides a real outage. Everything else goes to the router's
  // GenericError boundary, which reports the failure to App Insights.
  if (isError) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return <NotFound />;
    }
    throw error;
  }

  return (
    <div className='w-full'>
      <div className='dark:bg-c16 z-50 mt-1 mr-1 rounded-2xl bg-slate-100 bg-cover xl:sticky'>
        <div className='flex flex-col lg:flex-row lg:flex-wrap lg:items-start lg:justify-between'>
          <div className='flex items-center space-x-4 px-5 py-1'>
            <Link to='/'>
              <ArrowLeftIcon />
            </Link>
            <h2 className='inline font-bold'>
              {isLoading ? <Loader noMargin size='s' /> : data?.project.name}
            </h2>
            {data && <EstimationStageBadge stage={data.estimation.stage} isActive={false} />}
          </div>
          <div className='flex flex-wrap items-center gap-2 p-3 sm:gap-4'>
            {data && (
              <EstimationVersionDropdown projectId={data?.project.id} estimationId={estimationId} />
            )}
            <Button data-testid='close-button' variant='secondary' outlined onClick={onCloseClick}>
              {globalMessages.close()}
            </Button>
            {/* isAiEnabled alone is not enough: the AI Advisory seed model is AI-enabled with no
                AI request template, and a review without a template can never be produced.
                Same gate as the wizard's in NewEstimation/ProjectReview. */}
            {data &&
              estimationModel?.isAiEnabled &&
              !!estimationModel?.aiRequestTemplate?.length && (
                <>
                  <Button
                    variant='info'
                    outlined
                    onClick={() => setIsAiReviewParamsModalOpen(true)}
                  >
                    <IconCpuBolt className='mr-2' />
                    {reviewMessages.aiReviewEnqueueLabel()}
                  </Button>
                  <AiReviewParamsModal
                    estimationId={data.estimation.id}
                    estimationCultureName={data.estimation.cultureName}
                    size='m'
                    open={isAiReviewParamsModalOpen}
                    onClose={() => setIsAiReviewParamsModalOpen(false)}
                    onSubmit={handleEnqueueAiReview}
                  />
                </>
              )}
            {data &&
              data.estimation.stage === ApiEstimationStage.IN_PROCESS &&
              hasEstimationPermission('CAN_EDIT') && (
                <StartReviewButton estimationId={data.estimation.id} />
              )}
            {data &&
              data.estimation.stage === ApiEstimationStage.IN_REVIEW &&
              hasEstimationPermission('CAN_EDIT') && (
                <CancelReviewButton estimationId={data.estimation.id} />
              )}
            {data &&
              data.estimation.stage === ApiEstimationStage.IN_REVIEW &&
              hasEstimationPermission('CAN_APPROVE') && (
                <ApproveButton estimationId={data.estimation.id} />
              )}
            {buttons}
          </div>
        </div>
        <Tabs onTabChange={handleTabChange} items={tabsConfiguration} activeTabId={tabId} />
      </div>
      {/* EWM-1036: the model this estimation is bound to was archived. Informational only - the
          estimation stays fully editable. Rendered here so it shows on every tab. */}
      {estimationModel?.isArchived && <ArchivedModelBanner estimationId={estimationId} />}
      <div className='flex justify-center'>
        <div className='layout-tsx flex w-[100%] flex-col justify-center p-5'>{children}</div>
      </div>
    </div>
  );
};
